import React, { useEffect, useState } from 'react';
import Grid from '../game/grid/Grid';
import './WinModal.css';
import DonationLink from './Donation';
import {
  NUMBER_OF_HINTS,
  formatLongDisplayDate,
  getPlayerStreakStatistics,
  getShareText,
} from '../../helpers';
import {
  Box,
  Button,
  Modal,
  Popover,
  Typography,
  CircularProgress,
} from '@mui/material';
import { Leaderboard, Share, ArrowBackIosNew } from '@mui/icons-material/';
import PopupState, { bindPopover, bindTrigger } from 'material-ui-popup-state';
import { Statistics } from './Statistics';
import CountdownTimer from './CountdownTimer';
import { useGame } from '../../context/GameContext';
import { useNavigate } from 'react-router';

export default function WinModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const [showStatistics, setShowStatistics] = useState(false);
  const navigate = useNavigate();

  const {
    mode,
    gameState,
    gameSetupData,
    isPostingStats,
    statsError,
    statsData,
    statsRequest,
    setMode,
  } = useGame();

  useEffect(() => {
    setShowStatistics(false);
  }, [isOpen]);

  const isDemo = mode === 'demo';
  const isArchive = mode === 'archive';
  const isReal = mode === 'real';

  // Guard against missing game data
  if (!gameState || !gameSetupData) {
    return null;
  }

  const date = gameSetupData.date;
  const theme = gameSetupData.game.theme;

  // Top section
  const modalTopContent = (
    <>
      <Typography id="modal-modal-title" variant="h5" component="h2">
        Nice work, you win!
      </Typography>

      {!showStatistics && (
        <div className="theme">
          <Typography id="modal-modal-description" sx={{ mt: 2 }}>
            {`${isDemo ? 'The demo' : isArchive ? 'This' : "Today's"} theme is:`}
          </Typography>
          <Typography variant="h6" component={'p'}>
            {theme}
          </Typography>

          {!isDemo && (
            <Typography variant="body2">
              {formatLongDisplayDate(date)}
            </Typography>
          )}

          <div className="padding">
            <Grid gameState={gameState} isReadOnly={true} gridSize="small" />
          </div>
        </div>
      )}
    </>
  );

  const tryRealOnClick = () => {
    setMode('splash');
    navigate('/');
    onClose();
  };

  // Bottom section
  const modalBottomContent = () => {
    if (isDemo) {
      return (
        <Button variant="outlined" onClick={tryRealOnClick}>
          Try the real game
        </Button>
      );
    }

    if (statsError) {
      return (
        <>
          <div className="info-text-container">
            Sorry! There was an error posting statistics. Please check your
            internet connection or come back tomorrow.
          </div>
          <div className="button-container padding-top padding-bottom">
            <ShareButton />
          </div>
        </>
      );
    }

    if (isPostingStats) {
      return (
        <Box sx={{ display: 'flex' }}>
          <CircularProgress />
        </Box>
      );
    }

    if (showStatistics) {
      return (
        <>
          {statsData && statsRequest && (
            <Statistics
              hintsUsed={NUMBER_OF_HINTS - gameState.hintsRemaining}
              minMoves={gameState.path.length - 1}
              seconds={statsRequest.secondsToComplete}
              moves={gameState.moveCount}
              statisticPostData={statsData}
            />
          )}
          <StreakGroup />
          <div className="button-container padding-top padding-bottom">
            <BackButton />
            <ShareButton />
          </div>
          <DonationLink />
        </>
      );
    }

    const showStatisticsButton = statsData && !isDemo;

    return (
      <>
        {isReal && <CountdownTimer currentGameDate={date} />}
        {isReal && <StreakGroup />}
        {showStatisticsButton && (
          <div className="button-container padding-top padding-bottom">
            <ShowStatisticsButton />
            <ShareButton />
          </div>
        )}
        {isArchive && (
          <>
            <div className="info-text-container">
              You are playing an archive game.
            </div>
            <div className="button-container padding-top padding-bottom">
              <TodaysGameButton />
            </div>
          </>
        )}
        <DonationLink />
      </>
    );
  };

  function BackButton() {
    return (
      <Button
        variant="outlined"
        startIcon={<ArrowBackIosNew />}
        onClick={() => setShowStatistics(false)}
      >
        Back
      </Button>
    );
  }

  function TodaysGameButton() {
    const onClick = () => {
      onClose();
      navigate('/');
    };

    return (
      <Button variant="outlined" onClick={onClick}>
        Go to Today's Game
      </Button>
    );
  }

  function ShowStatisticsButton() {
    return (
      <Button
        variant="outlined"
        startIcon={<Leaderboard />}
        onClick={() => setShowStatistics(true)}
      >
        Statistics
      </Button>
    );
  }

  function ShareButton() {
    const shareButtonOnClick = () => {
      if (!gameState || !statsRequest) return;
      const text = getShareText(
        gameState?.moveCount,
        statsRequest?.secondsToComplete,
        NUMBER_OF_HINTS - gameState.hintsRemaining,
        date,
      );
      navigator.clipboard.writeText(text);
    };

    return (
      <PopupState variant="popover" popupId="demo-popup-popover">
        {(popupState) => {
          const { onClick, onTouchStart } = bindTrigger(popupState);
          const combinedOnClick = (event: React.MouseEvent) => {
            onClick(event);
            shareButtonOnClick();
            setTimeout(() => popupState.setOpen(false), 1000);
          };

          return (
            <div>
              <Button
                variant="outlined"
                startIcon={<Share />}
                onClick={combinedOnClick}
                onTouchStart={onTouchStart}
              >
                Share Results
              </Button>
              <Popover
                {...bindPopover(popupState)}
                anchorOrigin={{
                  vertical: 'center',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'center',
                  horizontal: -4,
                }}
              >
                <Typography sx={{ p: 1 }}>
                  Results copied to clipboard
                </Typography>
              </Popover>
            </div>
          );
        }}
      </PopupState>
    );
  }

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      className="modal"
    >
      <Box>
        {modalTopContent}
        {modalBottomContent()}
      </Box>
    </Modal>
  );
}

// --- Helper Components ---
function StreakGroup() {
  const stats = getPlayerStreakStatistics();

  return (
    <div className="statistic-block">
      <StreakBlock
        statisticName="Your streak"
        statisticNumber={stats.currentStreak}
      />
      <StreakBlock
        statisticName="Longest streak"
        statisticNumber={stats.longestStreak}
      />
      <StreakBlock
        statisticName="Games played"
        statisticNumber={stats.gamesPlayed}
      />
    </div>
  );
}

function StreakBlock({
  statisticName,
  statisticNumber,
}: {
  statisticName: string;
  statisticNumber: number;
}) {
  return (
    <div className="statistic-block-item">
      <Typography component="h3">{statisticName}</Typography>
      <Typography variant="h4" component="p">
        {statisticNumber}
      </Typography>
    </div>
  );
}
